import { formatReferenceCopy } from 'utils/typography'
import './manager-preparation-section.scss'
import {
  useState,
} from 'react'
import {
  useTranslation,
} from 'react-i18next'
import {
  Box,
  Button,
  Stack,
  Typography,
} from '@mui/material'

import ModalBig from '../../../../common/modalBig'

import ManagerCheckItemBlocks from './manager-check-item-blocks'
import ManagerExecutorFields, {
  managerExecutorSelectionInvalid,
} from './manager-executor-fields'

import {
  allApplicationCheckItemsCompleted,
} from '../check-block-utils'
import {
  showManagerPreparationTools,
} from '../detail-visibility'

const ManagerPreparationSection = ({
  app,
  managerView,
  checkStatuses,
  executorId,
  onExecutorIdChange,
  executorChangeReason,
  onExecutorChangeReasonChange,
  companyOptions,
  runAction,
  onUpdateCheckItem,
  onPostCheckItemComment,
  onUploadCheckItemDocument,
  onDeleteCheckItemDocument,
  onCompletePreparation,
  onAssignExecutor,
}: ApplicationManagerPreparationProps) => {
  const { t } = useTranslation()
  const [ completePreparationConfirmOpen, setCompletePreparationConfirmOpen ] = useState(false)
  const canCompletePreparation = allApplicationCheckItemsCompleted(app)
  const executorSelectionInvalid = managerExecutorSelectionInvalid({
    app,
    executorId,
    executorChangeReason,
  })

  const handleConfirmCompletePreparation = () => {
    if (executorSelectionInvalid) {
      return
    }
    setCompletePreparationConfirmOpen(false)
    runAction(async () => {
      await onCompletePreparation()
      await onAssignExecutor()
    }, 'applications__message__preparationCompleted')
  }

  return (
    <div className='application-manager-preparation-section'>
      {showManagerPreparationTools({ app: app, managerView: managerView }) ? (
        <Box className='application-manager-preparation-section__block'>
          <ManagerCheckItemBlocks
            app={app}
            publicId={app.public_id}
            checkStatuses={checkStatuses}
            runAction={runAction}
            onUpdateCheckItem={onUpdateCheckItem}
            onPostCheckItemComment={onPostCheckItemComment}
            onUploadDocument={onUploadCheckItemDocument}
            onDeleteDocument={onDeleteCheckItemDocument}
          />
          <Stack spacing={1}>
            <Button
              variant='contained'
              disabled={!canCompletePreparation}
              onClick={() => setCompletePreparationConfirmOpen(true)}
            >
              <Typography component='span' variant='inherit'>
                {formatReferenceCopy(t('applications__text__completePreparation'))}
              </Typography>
            </Button>
            {!canCompletePreparation && (
              <Typography variant='body2' sx={{ color: 'text.primary' }}>
                {formatReferenceCopy(t('applications__text__completePreparationAllBlocksRequired'))}
              </Typography>
            )}
          </Stack>
          <ModalBig
            isOpen={completePreparationConfirmOpen}
            onClose={() => setCompletePreparationConfirmOpen(false)}
            title={formatReferenceCopy(t('applications__text__completePreparationConfirmTitle'))}
            width={736}
            buttons={(
              <div className='application-manager-preparation-section__modal-actions'>
                <Button
                  variant='outlined'
                  sx={{ minWidth: '238px', marginRight: 3 }}
                  onClick={() => setCompletePreparationConfirmOpen(false)}
                >
                  <Typography component='span' variant='inherit'>
                    {formatReferenceCopy(t('actions__text__cancel'))}
                  </Typography>
                </Button>
                <Button
                  variant='contained'
                  fullWidth
                  disabled={executorSelectionInvalid}
                  onClick={handleConfirmCompletePreparation}
                >
                  <Typography component='span' variant='inherit'>
                    {formatReferenceCopy(t('applications__text__completePreparationConfirmAction'))}
                  </Typography>
                </Button>
              </div>
            )}
          >
            <Typography variant='body1' className='application-manager-preparation-section__modal-description'>
              {formatReferenceCopy(t('applications__text__completePreparationConfirmBody'))}
            </Typography>
            <Typography variant='body2' sx={{ mt: 2, color: 'text.primary' }}>
              {formatReferenceCopy(t('applications__text__completePreparationExecutorHint'))}
            </Typography>
            <ManagerExecutorFields
              app={app}
              executorId={executorId}
              onExecutorIdChange={onExecutorIdChange}
              executorChangeReason={executorChangeReason}
              onExecutorChangeReasonChange={onExecutorChangeReasonChange}
              companyOptions={companyOptions}
            />
          </ModalBig>
        </Box>
      ) : (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {formatReferenceCopy(t('applications__text__mgrStep_preparationDone'))}
        </Typography>
      )}
    </div>
  )
}
export default ManagerPreparationSection
